import { useState } from "react";
import { all } from "ramda";
import { isItemSelected, getFinalFilterSets } from "../TxnHistory/utils";
import { useSettings } from "utils/settingsContext";

/**
 * Filters
 * @typedef Filters
 * @property {Array<SelectableTransactionFilter>} types
 *
 */

/**
 * useTransactionFilters
 * @param {Object.<string, Filters>} initialFilters
 * @returns {object} useTransactionFilters filter state and state updaters.
 */
export default function useTransactionFilters(initialFilters) {
  // This state management should be upgraded to useReducer for state management
  // when extended to handle multiple filter/query params.
  const { appSettings, integratorSettings } = useSettings();

  // by default, use the global setting list of transaction types to omit from UI
  let transactionTypesToOmit = appSettings.queryTransactions?.transaction_types_to_omit_from_query;

  // if there is a publisher specific setting, use that instead
  if (
    integratorSettings.queryTransactions &&
    integratorSettings.queryTransactions.transaction_types_to_omit_from_query &&
    integratorSettings.queryTransactions.transaction_types_to_omit_from_query.length
  ) {
    transactionTypesToOmit =
      integratorSettings.queryTransactions.transaction_types_to_omit_from_query;
  }

  // get the new filters by filtering out the ones we want to hide from the UI
  const finalFilterSets = getFinalFilterSets(initialFilters, transactionTypesToOmit);

  const [filterSets, setFilterStates] = useState(finalFilterSets);

  const toggleTypeBySet = (setName, selectedType) => {
    const setAllUnselected = (types) => types.map((type) => ({ ...type, selected: false }));

    const toggleFilter = (types) => {
      const updatedSelection = types.map((type) => ({
        ...type,
        selected: type.value === selectedType ? !type.selected : type.selected,
      }));
      return all(isItemSelected, updatedSelection) ? setAllUnselected(types) : updatedSelection;
    };

    setFilterStates((filterStates) => {
      const types = filterStates[setName].types;
      const updatedTypes = selectedType === "all" ? setAllUnselected(types) : toggleFilter(types);
      return {
        ...filterStates,
        [setName]: {
          ...filterStates[setName],
          types: updatedTypes,
        },
      };
    });
  };

  return {
    filterSets,
    toggleTypeBySet,
  };
}
